import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { QuestionnaireQuestion, QuestionnaireSection } from '@/types/generated'

export const useQuestionnaireStore = defineStore('questionnaireStore', () => {
  const formData = ref({} as Record<string, any>)
  const activeFieldId = ref('')
  const activeFieldIndex = ref(0)
  const flatFields = ref<QuestionnaireQuestion[]>([])
  const rawSchema = ref<QuestionnaireSection[]>([])

  // Computed properties
  const formLength = computed(() => flatFields.value.length)
  const isLastField = computed(() => activeFieldIndex.value === flatFields.value.length)
  const isFirstField = computed(() => activeFieldIndex.value === 0)

  function updateFormData(fieldId: string, value: any) {
    formData.value[fieldId] = value
  }

  function setFormData(data: Record<string, any>) {
    formData.value = data
  }

  function initializeForm(schema: QuestionnaireSection[]) {
    rawSchema.value = schema
    flatFields.value = []

    if (Array.isArray(schema)) {
      schema.forEach((page) => {
        if (Array.isArray(page.items)) {
          page.items.forEach((field) => {
            flatFields.value.push(field as QuestionnaireQuestion)
            if (field.value !== undefined && field.value !== '') {
              updateFormData(field.uuid, field.value)
            }
          })
        }
      })
    }

    // Set initial active field
    let initialActiveField = flatFields.value[0] || {}
    for (let i = 0; i < flatFields.value.length; i++) {
      if (
        formData.value[flatFields.value[i].uuid] === undefined ||
        formData.value[flatFields.value[i].uuid] === '' ||
        formData.value[flatFields.value[i].uuid] === null
      ) {
        initialActiveField = flatFields.value[i]
        break
      }
    }

    const initialActiveIndex = flatFields.value.findIndex(
      (field) => field.uuid === initialActiveField.uuid
    )
    activeFieldIndex.value = initialActiveIndex !== -1 ? initialActiveIndex : 0
    activeFieldId.value = initialActiveField.uuid || ''
  }

  function jumpToField(uuid: string) {
    const index = flatFields.value.findIndex((field) => field.uuid === uuid)
    if (index !== -1) {
      activeFieldIndex.value = index
      activeFieldId.value = uuid
    }
  }

  function goToNextField() {
    const nextIndex = findNextField(activeFieldIndex.value)
    activeFieldIndex.value = nextIndex
    if (!isLastField.value) {
      activeFieldId.value = flatFields.value[nextIndex]?.uuid || ''
    }
  }

  function goToPreviousField() {
    if (activeFieldIndex.value > 0) {
      activeFieldIndex.value--
      activeFieldId.value = flatFields.value[activeFieldIndex.value]?.uuid || ''
    }
  }

  function findNextField(currentIndex: number): number {
    const currentField = flatFields.value[currentIndex]

    if (currentField?.jump_to && currentField.jump_to.length > 0) {
      const jumpCondition = currentField.jump_to.find((jump) => {
        if (currentField.type === 'yesno') {
          const formDataValue = formData.value[currentField.uuid]
          const jumpValueLower = jump.value.toLowerCase()
          return (
            ((jumpValueLower === 'yes' || jumpValueLower === 'ja') && formDataValue === true) ||
            ((jumpValueLower === 'no' || jumpValueLower === 'nein') && formDataValue === false)
          )
        }
        return jump.value === formData.value[currentField.uuid]
      })
      if (jumpCondition) {
        if (jumpCondition.uuid === 'END') {
          return flatFields.value.length // Jump to the last field
        }
        const jumpIndex = flatFields.value.findIndex((field) => field.uuid === jumpCondition.uuid)
        if (jumpIndex !== -1) {
          return jumpIndex
        }
      }
    }
    return currentIndex + 1
  }

  function getFieldByUuid(uuid: string) {
    return flatFields.value.find((field) => field.uuid === uuid)
  }

  return {
    formData,
    activeFieldId,
    activeFieldIndex,
    flatFields,
    formLength,
    isLastField,
    isFirstField,
    updateFormData,
    setFormData,
    initializeForm,
    jumpToField,
    goToNextField,
    goToPreviousField,
    getFieldByUuid
  }
})
